/**
 * 华为应用商店：目标费率与成本配置 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 src.huawei.dtos.feilv_target_config 对齐。
 *   - GET /api/v1/huawei/feilv-target-config  → 读配置当前态（任意登录用户可读；
 *     表无行 / DDL 未执行时后端 fail-open 返回默认态 custom + 0.01 + 成本 4000）
 *   - PUT /api/v1/huawei/feilv-target-config  → 保存配置（仅 reizhu /
 *     v_summerxiao / admin / devinzhang，用户组同 huawei:strategy，否则后端 403；
 *     模式/取值不合法后端 400；写库失败 500）
 *
 * 与 Apple Ads 域 services/apple/feilvTargetConfig.ts 的差异（PR-HW1-20261008-001）：
 *   - 接口路径换为 /api/v1/huawei/feilv-target-config；
 *   - 华为侧无盯盘指标 cost_ent_appl 引用，成本口径为通用「目标企业进件成本」；
 *   - 其余类型与行为（custom 模式 / 区间 / 字段集）与 apple 版一致。
 */

import { request } from "../apiClient";

/**
 * 目标费率取值模式（与后端 FEILV_MODE_* 常量对齐）。
 * sxfl_3d/sxfl_7d 为 DDL CHECK 预留枚举（跟随链路平均费率），当前未启用——
 * 保存仅接受 custom；Record 侧保留联合类型以如实承载库内可能的预留值。
 */
export type FeilvMode = "custom" | "sxfl_3d" | "sxfl_7d";

/** 目标费率与成本配置当前态（与后端 FeilvTargetConfigRecord 对齐；None 字段经 exclude_none 不下发）。 */
export interface FeilvTargetConfigRecord {
  config_key: string;
  feilv_mode: FeilvMode;
  /** custom 模式固定值（小数费率，如 0.01=1%）。 */
  feilv_value: number;
  /** 目标企业进件成本（元，默认 4000）。 */
  cost_value: number;
  create_user?: string;
  modify_user?: string;
  created_time?: string;
  updated_time?: string;
}

export interface SaveFeilvTargetConfigBody {
  /** 当前仅接受 "custom"（后端 service 层校验，其余值 400）。 */
  feilv_mode: "custom";
  /** custom 模式固定值（(0,1) 开区间小数费率）。 */
  feilv_value: number;
  /** 目标企业进件成本（元，(0,20000) 开区间）。 */
  cost_value: number;
}

/** 读取目标费率与成本配置当前态；表无行 / DDL 未执行时后端返回默认态 custom + 0.01 + 成本 4000。 */
export async function getFeilvTargetConfig(): Promise<FeilvTargetConfigRecord | null> {
  return request<FeilvTargetConfigRecord | null>(
    "/api/v1/huawei/feilv-target-config",
  );
}

/** 保存目标费率与成本配置；返回保存后当前态（含更新后的审计列）。非白名单用户后端返回 403。 */
export async function saveFeilvTargetConfig(
  body: SaveFeilvTargetConfigBody,
): Promise<FeilvTargetConfigRecord> {
  return request<FeilvTargetConfigRecord>(
    "/api/v1/huawei/feilv-target-config",
    {
      method: "PUT",
      body,
    },
  );
}
